import React, { useEffect, useState, useMemo } from "react";
import { Card, CardBody, CardHeader, Col, Row, Input, Table, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { listOrder } from "../Orders/_requests";
import { listAllPausedSubscription } from "../Subscription/_requests";
import { listProduct } from "../Products/_requests";

const DailyDeliveryOverview = () => {
    const [orders, setOrders] = useState<any[]>([]);
    const [pausedSubscriptions, setPausedSubscriptions] = useState<any[]>([]);
    const [productMap, setProductMap] = useState<{ [key: string]: string }>({});
    const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [selectedSlot, setSelectedSlot] = useState("All");
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();

    const toggleModal = () => setIsModalOpen(!isModalOpen);

    const resolveCategory = (product: any): string => {
        return product?.categories?.name ||
            (Array.isArray(product?.categories) ? product?.categories[0]?.name : product?.category?.name) ||
            productMap[product?.id?.toString()] ||
            "Other";
    };

    const isWeightBased = (category: string, variantName: string): boolean => {
        return category === "Weight" || /^(kg|g|kilogram|gram|grams)$/i.test(variantName);
    };

    const handleCardClick = (type?: number) => {
        if (type !== undefined) {
            navigate("/orders", { state: { selectedDate, type } });
        } else {
            const element = document.getElementById("paused-subscriptions-section");
            if (element) {
                element.scrollIntoView({ behavior: "smooth" });
            }
        }
    };

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [ordersRes, pausedRes, productsRes]: any = await Promise.all([
                listOrder(),
                listAllPausedSubscription(),
                listProduct()
            ]);
            const ordersData = ordersRes;
            console.log("ordersRes========", ordersRes);
            console.log("pausedRes========", pausedRes);
            console.log("productsRes========", productsRes);
            setOrders(Array.isArray(ordersData) ? ordersData : (ordersData?.data || []));

            const pausedData = pausedRes;
            setPausedSubscriptions(Array.isArray(pausedData) ? pausedData : (pausedData?.data || []));

            const mapping: { [key: string]: string } = {};
            productsRes.data?.forEach((prod: any) => {
                const catName = prod.categories?.name || (Array.isArray(prod.categories) ? prod.categories[0]?.name : prod.category?.name);
                if (prod.id && catName) {
                    mapping[prod.id.toString()] = catName;
                }
            });
            setProductMap(mapping);
        } catch (error) {
            console.error("Error fetching data for overview:", error);
        } finally {
            setIsLoading(false);
        }
    };
    console.log("orders========", orders);
    const categories = useMemo(() => {
        const catSet = new Set<string>();
        orders.forEach(order => {
            order.orderItems?.forEach((item: any) => {
                const catName = resolveCategory(item.product);
                if (catName) catSet.add(catName);
            });
        });
        pausedSubscriptions.forEach(p => {
            p.userSubscription?.details?.forEach((detail: any) => {
                const catName = resolveCategory(detail.product);
                if (catName) catSet.add(catName);
            });
        });
        return ["All", ...Array.from(catSet)];
    }, [orders, pausedSubscriptions, productMap]);

    useEffect(() => {
        fetchData();
    }, []);

    const dailyStats = useMemo(() => {
        console.log("orders==========", orders);
        // console.log("pausedSubscriptions", pausedSubscriptions);
        // 1. Filter orders for selected date and category
        const filteredOrders = orders.filter((order: any) => {
            // Let's show everything that matches the date to start with
            // and see if data appears.
            if (!order.delivery_date) return false;
            // Only filter by date and category/slot. Status filtering can be added back if needed, 
            // but let's first ensure data is appearing.
            const matchesDate = moment(order.delivery_date).format("YYYY-MM-DD") === selectedDate;


            const matchesSlot = selectedSlot === "All" || String(order.slot_type) === selectedSlot;
            const matchesCategory = selectedCategory === "All" || order.orderItems?.some((item: any) => {
                return resolveCategory(item.product) === selectedCategory;
            });
            return matchesDate && matchesCategory && matchesSlot;
        });

        // 2. Filter paused subscriptions for selected date and category
        const filteredPaused = pausedSubscriptions.filter((p: any) => {
            if (!p.date) return false;
            const matchesDate = moment(p.date).format("YYYY-MM-DD") === selectedDate;

            //const matchesDate = moment(p.date).isSame(moment(selectedDate), 'day');
            const matchesSlot = selectedSlot === "All" || String(p.userSubscription?.slot_type) === selectedSlot;
            const matchesCategory = selectedCategory === "All" || p.userSubscription?.details?.some((detail: any) => {
                return resolveCategory(detail.product) === selectedCategory;
            });
            return matchesDate && matchesCategory && matchesSlot;
        });

        const productBreakdown: { [key: string]: any } = {};

        let adHocQty = 0;
        let adHocVolume = 0;
        let adHocVolumeQty = 0;
        let adHocWeight = 0;
        let adHocWeightQty = 0;

        let plannedSubQty = 0;
        let plannedSubVolume = 0;
        let plannedSubVolumeQty = 0;
        let plannedSubWeight = 0;
        let plannedSubWeightQty = 0;

        let pausedQty = 0;
        let pausedVolume = 0;
        let pausedVolumeQty = 0;
        let pausedWeight = 0;
        let pausedWeightQty = 0;

        // Process Ad-hoc and Subscription Orders from listOrder
        filteredOrders.forEach((order: any) => {
            order.orderItems?.forEach((item: any) => {
                const variant = item.variant;
                const variantName = (variant?.name || "").trim();
                const category = resolveCategory(item.product);

                if (selectedCategory !== "All" && category !== selectedCategory) return;

                const qty = Number(item.quantity) || 0;
                const variantValue = Number(item.variant_value) || 0;
                const productName = `${item.product?.name}` || "Unknown";
                const routeName = order.route?.name || "No Route";
                const key = `${routeName}||${productName}-${variantValue}-${variantName}`;

                const isVolumeLarge = /^(l|ltr|liter|litres)$/i.test(variantName);
                const isWeightLarge = /^(kg|kilogram|kilograms)$/i.test(variantName);
                const isLargeUnit = isVolumeLarge || isWeightLarge;

                const isWeight = isWeightBased(category, variantName);

                const baseValue = isLargeUnit ? variantValue : variantValue / 1000;
                const totalBaseAmount = baseValue * qty;

                if (!productBreakdown[key]) {
                    productBreakdown[key] = {
                        qty: 0,
                        liters: 0,
                        subQty: 0,
                        subLiters: 0,
                        orderQty: 0,
                        orderLiters: 0,
                        pausedQty: 0,
                        pausedLiters: 0,
                        productName,
                        variantName,
                        variantValue,
                        isWeight,
                        category,
                        routeName
                    };
                }

                if (order.type === 2) {
                    productBreakdown[key].subQty += qty;
                    productBreakdown[key].subLiters += totalBaseAmount;
                    plannedSubQty += qty;
                    if (isWeight) {
                        plannedSubWeight += totalBaseAmount;
                        plannedSubWeightQty += qty;
                    } else {
                        plannedSubVolume += totalBaseAmount;
                        plannedSubVolumeQty += qty;
                    }
                } else {
                    productBreakdown[key].orderQty += qty;
                    productBreakdown[key].orderLiters += totalBaseAmount;
                    adHocQty += qty;
                    if (isWeight) {
                        adHocWeight += totalBaseAmount;
                        adHocWeightQty += qty;
                    } else {
                        adHocVolume += totalBaseAmount;
                        adHocVolumeQty += qty;
                    }
                }

                productBreakdown[key].qty += qty;
                productBreakdown[key].liters += totalBaseAmount;
            });
        });

        // Process Paused Subscriptions to subtract
        filteredPaused.forEach((pausedItem: any) => {
            pausedItem.userSubscription?.details?.forEach((detail: any) => {
                console.log("detail========", detail)
                const variant = detail.variant;
                const variantName = (variant?.name || `${detail.variant_value || ""}`).trim();
                const category = resolveCategory(detail.product);

                if (selectedCategory !== "All" && category !== selectedCategory) return;

                const qty = Number(detail.quantity) || 1;
                //const variantValue = Number(detail.variant_value) || 0;
                const variantValue = Number(detail.product?.variants[0]
                    ?.variant_value) || 0;

                const productName = detail.product?.name || "Unknown";
                const routeName = pausedItem.userSubscription?.route?.name || "No Route";
                const key = `${routeName}||${productName}-${variantValue}-${variantName}`;

                const isVolumeLarge = /^(l|ltr|liter|litres)$/i.test(variantName);
                const isWeightLarge = /^(kg|kilogram|kilograms)$/i.test(variantName);
                const isLargeUnit = isVolumeLarge || isWeightLarge;

                const isWeight = isWeightBased(category, variantName);

                const baseValue = isLargeUnit ? variantValue : variantValue / 1000;
                const totalBaseAmount = baseValue * qty;

                if (!productBreakdown[key]) {
                    productBreakdown[key] = {
                        qty: 0,
                        liters: 0,
                        subQty: 0,
                        subLiters: 0,
                        orderQty: 0,
                        orderLiters: 0,
                        pausedQty: 0,
                        pausedLiters: 0,
                        productName,
                        variantName,
                        variantValue,
                        isWeight,
                        category
                    };
                }

                productBreakdown[key].pausedQty += qty;
                productBreakdown[key].pausedLiters += totalBaseAmount;
                pausedQty += qty;
                if (isWeight) {
                    pausedWeight += totalBaseAmount;
                    pausedWeightQty += qty;
                } else {
                    pausedVolume += totalBaseAmount;
                    pausedVolumeQty += qty;
                }
            });
        });

        const netSubQty = plannedSubQty;
        const netSubVolume = plannedSubVolume;
        const netSubVolumeQty = plannedSubVolumeQty;
        const netSubWeight = plannedSubWeight;
        const netSubWeightQty = plannedSubWeightQty;

        const categoryBreakdown: { [key: string]: { vol: number, wgt: number, isWeight: boolean } } = {};
        Object.values(productBreakdown).forEach((item: any) => {
            const cat = item.category || "Other";
            if (!categoryBreakdown[cat]) {
                categoryBreakdown[cat] = { vol: 0, wgt: 0, isWeight: item.isWeight };
            }
            const netVol = item.orderLiters + (item.subLiters - item.pausedLiters);
            if (item.isWeight) {
                categoryBreakdown[cat].wgt += netVol;
            } else {
                categoryBreakdown[cat].vol += netVol;
            }
        });

        return {
            adHocQty,
            adHocVolume: adHocVolume.toFixed(2),
            adHocVolumeQty,
            adHocWeight: adHocWeight.toFixed(2),
            adHocWeightQty,

            plannedSubQty,
            plannedSubVolume: plannedSubVolume.toFixed(2),
            plannedSubWeight: plannedSubWeight.toFixed(2),

            pausedQty,
            pausedVolume: pausedVolume.toFixed(2),
            pausedVolumeQty,
            pausedWeight: pausedWeight.toFixed(2),
            pausedWeightQty,

            netSubQty,
            netSubVolume: netSubVolume.toFixed(2),
            netSubVolumeQty,
            netSubWeight: netSubWeight.toFixed(2),
            netSubWeightQty,

            totalNetQty: adHocQty + netSubQty,
            totalNetVolume: (adHocVolume + netSubVolume).toFixed(2),
            totalNetVolumeQty: adHocVolumeQty + netSubVolumeQty,
            totalNetWeight: (adHocWeight + netSubWeight).toFixed(2),
            totalNetWeightQty: adHocWeightQty + netSubWeightQty,

            breakdown: Object.values(productBreakdown).sort((a: any, b: any) => {
                if (a.routeName !== b.routeName) return a.routeName?.localeCompare(b.routeName);
                return a.productName?.localeCompare(b.productName);
            }),
            categoryBreakdown
        };
    }, [orders, pausedSubscriptions, selectedDate, selectedCategory, selectedSlot]);

    return (
        <React.Fragment>
            <Col xl={12}>
                <Card>
                    <CardHeader className="border-0 align-items-center d-flex">
                        <h4 className="card-title mb-0 flex-grow-1">
                            Daily Delivery Overview
                            <small className="ms-2 text-muted" style={{ fontSize: '10px' }}>
                                (Raw: {orders.length} orders, {pausedSubscriptions.length} paused)
                            </small>
                        </h4>
                        <div className="flex-shrink-0 d-flex gap-2">
                            <Input
                                type="select"
                                className="form-select-sm"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </Input>
                            <Input
                                type="select"
                                className="form-select-sm"
                                value={selectedSlot}
                                onChange={(e) => setSelectedSlot(e.target.value)}
                            >
                                <option value="All">All Slots</option>
                                <option value="1">Morning Slot</option>
                                <option value="2">Evening Slot</option>
                            </Input>
                            <Input
                                type="date"
                                className="form-control-sm"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                            />
                            <Button
                                color="primary"
                                size="sm"
                                onClick={toggleModal}
                                className="d-flex align-items-center gap-1"
                            >
                                <i className="ri-download-2-line"></i> Download
                            </Button>
                        </div>
                    </CardHeader>
                    <CardBody>
                        <Row className="g-3 mb-4">
                            <Col md={3}>
                                <div
                                    className="p-3 border border-dashed rounded bg-primary bg-opacity-10"
                                    onClick={() => handleCardClick(1)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="d-flex align-items-center mb-2">
                                        <h3 className="mb-0 fw-bold text-primary">{dailyStats.adHocQty}</h3>
                                        <span className="ms-2 badge bg-primary">Items</span>
                                    </div>
                                    <div className="d-flex gap-3">
                                        <div>
                                            <p className="text-muted mb-0 small text-uppercase">Volume</p>
                                            <h5 className="mb-0 fw-bold">{dailyStats.adHocVolume} L <br /><small className="text-muted" style={{ fontSize: '11px' }}>({dailyStats.adHocVolumeQty} nos)</small></h5>
                                        </div>
                                        <div>
                                            <p className="text-muted mb-0 small text-uppercase">Weight</p>
                                            <h5 className="mb-0 fw-bold">{dailyStats.adHocWeight} Kg <br /><small className="text-muted" style={{ fontSize: '11px' }}>({dailyStats.adHocWeightQty} nos)</small></h5>
                                        </div>
                                    </div>
                                    <p className="text-primary mt-2 mb-0 small fw-bold text-uppercase">Total Order Delivery</p>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div
                                    className="p-3 border border-dashed rounded bg-info bg-opacity-10"
                                    onClick={() => handleCardClick(2)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="d-flex align-items-center mb-2">
                                        <h3 className="mb-0 fw-bold text-info">{dailyStats.netSubQty}</h3>
                                        <span className="ms-2 badge bg-info">Items</span>
                                    </div>
                                    <div className="d-flex gap-3">
                                        <div>
                                            <p className="text-muted mb-0 small text-uppercase">Volume</p>
                                            <h5 className="mb-0 fw-bold">{dailyStats.netSubVolume} L <br /><small className="text-muted" style={{ fontSize: '11px' }}>({dailyStats.netSubVolumeQty} nos)</small></h5>
                                        </div>
                                        <div>
                                            <p className="text-muted mb-0 small text-uppercase">Weight</p>
                                            <h5 className="mb-0 fw-bold">{dailyStats.netSubWeight} Kg <br /><small className="text-muted" style={{ fontSize: '11px' }}>({dailyStats.netSubWeightQty} nos)</small></h5>
                                        </div>
                                    </div>
                                    <p className="text-info mt-2 mb-0 small fw-bold text-uppercase">Total Subscription Delivery</p>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div
                                    className="p-3 border border-dashed rounded bg-danger bg-opacity-10"
                                    onClick={() => handleCardClick()}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="d-flex align-items-center mb-2">
                                        <h3 className="mb-0 fw-bold text-danger">{dailyStats.pausedQty}</h3>
                                        <span className="ms-2 badge bg-danger">Items</span>
                                    </div>
                                    <div className="d-flex gap-3">
                                        <div>
                                            <p className="text-muted mb-0 small text-uppercase">Volume</p>
                                            <h5 className="mb-0 fw-bold">{dailyStats.pausedVolume} L <br /><small className="text-muted" style={{ fontSize: '11px' }}>({dailyStats.pausedVolumeQty} nos)</small></h5>
                                        </div>
                                        <div>
                                            <p className="text-muted mb-0 small text-uppercase">Weight</p>
                                            <h5 className="mb-0 fw-bold">{dailyStats.pausedWeight} Kg <br /><small className="text-muted" style={{ fontSize: '11px' }}>({dailyStats.pausedWeightQty} nos)</small></h5>
                                        </div>
                                    </div>
                                    <p className="text-danger mt-2 mb-0 small fw-bold text-uppercase">Paused Subscriptions</p>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div
                                    className="p-3 border border-dashed rounded bg-success bg-opacity-10"
                                    onClick={() => handleCardClick(0)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="d-flex align-items-center mb-2">
                                        <h3 className="mb-0 fw-bold text-success">{dailyStats.totalNetQty}</h3>
                                        <span className="ms-2 badge bg-success">Items</span>
                                    </div>
                                    <div className="d-flex gap-3">
                                        <div>
                                            <p className="text-success mb-0 small text-uppercase">Grand Vol</p>
                                            <h4 className="mb-0 fw-bold text-success">{dailyStats.totalNetVolume} L <br /><small style={{ fontSize: '11px' }}>({dailyStats.totalNetVolumeQty} nos)</small></h4>
                                        </div>
                                        <div>
                                            <p className="text-success mb-0 small text-uppercase">Grand Wgt</p>
                                            <h4 className="mb-0 fw-bold text-success">{dailyStats.totalNetWeight} Kg <br /><small style={{ fontSize: '11px' }}>({dailyStats.totalNetWeightQty} nos)</small></h4>
                                        </div>
                                    </div>
                                    <p className="text-success mt-2 mb-0 small fw-bold text-uppercase">Grand Total Delivery</p>
                                </div>
                            </Col>
                        </Row>

                        <div className="table-responsive">
                            <Table className="align-middle table-nowrap mb-0 table-sm">
                                <thead className="table-light">
                                    <tr>
                                        <th>Route</th>
                                        <th>Product</th>
                                        <th>Variant</th>
                                        <th className="text-center">Orders</th>
                                        <th className="text-center">Sub (Net)</th>
                                        <th className="text-center">Paused</th>
                                        <th className="text-end">Total Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dailyStats.breakdown.length > 0 ? (
                                        dailyStats.breakdown.map((item: any, index: number) => {
                                            console.log("itm=========", item)
                                            const netSub = item.subQty - item.pausedQty;
                                            const totalItemLiters = item.orderLiters + (item.subLiters - item.pausedLiters);
                                            const unitLabel = item.isWeight ? "Kg" : "L";
                                            return (
                                                <tr key={index}>
                                                    <td className="fw-bold text-primary">{item.routeName}</td>
                                                    <td>{item.productName}</td>
                                                    <td>{item.variantValue} {item.variantName}</td>
                                                    <td className="text-center">{item.orderQty}</td>
                                                    <td className="text-center">
                                                        {item.subQty}
                                                    </td>
                                                    <td className="text-center text-danger">{item.pausedQty}</td>
                                                    <td className="text-end fw-medium">
                                                        {(item.liters).toFixed(2)} {item.isWeight ? 'Kg' : 'L'}
                                                        <br />
                                                        <small className="text-muted">({item.orderQty + item.subQty} nos)</small>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={6} className="text-center text-muted">
                                                No deliveries scheduled for this date.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </CardBody>
                </Card>
            </Col>
            <OverviewModal
                isOpen={isModalOpen}
                toggle={toggleModal}
                stats={dailyStats}
                date={selectedDate}
            />
        </React.Fragment>
    );
};

const OverviewModal = ({ isOpen, toggle, stats, date }: { isOpen: boolean, toggle: () => void, stats: any, date: string }) => {
    const handlePrint = () => {
        window.print();
    };

    const adHocItems = stats.breakdown.filter((item: any) => item.orderQty > 0);
    const subItems = stats.breakdown.filter((item: any) => (item.subQty - item.pausedQty) > 0);
    const totalItems = stats.breakdown.filter((item: any) => (item.orderQty + (item.subQty - item.pausedQty)) > 0);

    const formatValue = (value: number, isWeight: boolean) => {
        if (isWeight) {
            return value >= 1 ? `${value.toFixed(1)} kg` : `${(value * 1000).toFixed(0)} g`;
        } else {
            return value >= 1 ? `${value.toFixed(1)} L` : `${(value * 1000).toFixed(0)} ml`;
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" className="order-overview-modal">
            <ModalHeader toggle={toggle}>
                Daily Delivery Overview - {moment(date).format("DD MMM, YYYY")} (Generated at: {moment().format("hh:mm A")})
            </ModalHeader>
            <ModalBody id="printable-area">
                <style>
                    {`
                    @media print {
                        /* Limit body height to 1 page and hide its content by default */
                        html, body {
                            height: 100vh !important;
                            overflow: hidden !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            visibility: hidden !important;
                        }
                        /* Show only the printable area using absolute positioning to pin it to page 1 */
                        #printable-area {
                            visibility: visible !important;
                            display: block !important;
                            position: absolute !important;
                            left: 0 !important;
                            top: 0 !important;
                            width: 100% !important;
                            min-height: 100% !important;
                            padding: 40px !important;
                            margin: 0 !important;
                            background: white !important;
                            z-index: 10000 !important;
                        }
                        /* Ensure all children of the printable area are also visible */
                        #printable-area * {
                            visibility: visible !important;
                        }
                        /* Hide things that shouldn't be printed even within the modal */
                        .no-print, .modal-header, .modal-footer, .btn-close {
                            display: none !important;
                        }
                    }
                    `}
                </style>
                <div className="d-none d-print-block mb-4 border-bottom pb-2">
                    <h4 className="fw-bold">Daily Delivery Overview - {moment(date).format("DD MMM, YYYY")}</h4>
                    <p className="text-muted mb-0">Generated at: {moment().format("hh:mm A")}</p>
                </div>
                <Row className="g-4">
                    {/* Orders Section */}
                    <Col md={6}>
                        <div className="p-3 border rounded h-100">
                            <h5 className="text-primary border-bottom pb-2 mb-3">Order</h5>
                            <div className="mb-3">
                                <h4 className="fw-bold mb-0">{stats.adHocQty} - Nos</h4>
                            </div>
                            <ul className="list-unstyled mb-0">
                                {adHocItems.map((item: any, idx: number) => {
                                    return (
                                        <li key={idx} className="mb-3">
                                            <div className="d-flex justify-content-between fw-bold">
                                                <span>{item.routeName} - {item.productName}</span>
                                                <span className="text-primary">: {formatValue(item.orderLiters, item.isWeight)}</span>
                                            </div>
                                            <small className="text-dark d-block" style={{ fontSize: "10px" }}>
                                                ({item.variantValue}{item.variantName} x {item.orderQty} qty)
                                            </small>
                                        </li>
                                    );
                                })}
                            </ul>

                        </div>
                    </Col>

                    {/* Subscriptions Section */}
                    <Col md={6}>
                        <div className="p-3 border rounded h-100">
                            <h5 className="text-info border-bottom pb-2 mb-3">Subscription</h5>
                            <div className="mb-3">
                                <h4 className="fw-bold mb-0">{stats.netSubQty} - Nos</h4>
                            </div>
                            <ul className="list-unstyled mb-0">
                                {subItems.map((item: any, idx: number) => {
                                    const netQty = item.subQty - item.pausedQty;
                                    const netLiters = item.subLiters - item.pausedLiters;
                                    return (
                                        <li key={idx} className="mb-3">
                                            <div className="d-flex justify-content-between fw-bold">
                                                <span>{item.routeName} - {item.productName}</span>
                                                <span className="text-info">: {formatValue(netLiters, item.isWeight)}</span>
                                            </div>
                                            <small className="text-dark d-block" style={{ fontSize: "10px" }}>
                                                ({item.variantValue}{item.variantName} x {item.subQty} qty {item.pausedQty > 0 ? `- ${item.pausedQty} paused` : ''})
                                            </small>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </Col>

                    {/* Total Section */}
                    <Col md={12}>
                        <div className="p-3 border rounded bg-light">
                            <h5 className="text-success border-bottom pb-2 mb-3">Total</h5>
                            <div className="mx-auto" style={{ maxWidth: "400px" }}>
                                <ul className="list-unstyled mb-0">
                                    {Object.entries(stats.categoryBreakdown || {}).filter(([cat, data]: [string, any]) => (data.vol > 0 || data.wgt > 0)).map(([cat, data]: [string, any], idx) => (
                                        <li key={idx} className="mb-2 d-flex justify-content-between fw-bold">
                                            <span>{cat}</span>
                                            <span className="text-success">: {formatValue(data.isWeight ? data.wgt : data.vol, data.isWeight)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter className="no-print">
                <Button color="secondary" onClick={toggle}>Close</Button>
                <Button color="primary" onClick={handlePrint}>
                    <i className="ri-printer-line me-1"></i> Print
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default DailyDeliveryOverview;
